// src/pages/CreateTripPage.tsx
import React, { useState } from 'react';
import { View, Heading, TextField, TextAreaField, Button, Flex, Divider, Text } from '@aws-amplify/ui-react';
import { Amplify } from 'aws-amplify';
import awsExports from '../aws-exports';
import { generateClient } from 'aws-amplify/api';
import { createTrip, createTripMember } from '../graphql/mutations';
import { getTrip } from '../graphql/queries';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { TripRole } from '../API'; // Import the TripRole enum from your generated types
import { Film, Calendar, MapPin, AlignLeft, ChevronLeft } from 'lucide-react';

Amplify.configure(awsExports);
const client = generateClient();

const CreateTripPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formState, setFormState] = useState({
    tripName: '',
    description: '',
    startDate: '',
    endDate: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formState.tripName.trim()) {
      newErrors.tripName = 'Album title is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      const tripInput = {
        tripName: formState.tripName,
        description: formState.description,
        startDate: formState.startDate || null,
        endDate: formState.endDate || null,
        createdBy: user?.sub
      };
      
      const result: any = await client.graphql({query: createTrip, variables: { input: tripInput }, authMode: "userPool"});
      const newTrip = result.data.createTrip;
      
      // Add the current user as a PARTICIPANT
      const tripMemberInput = {
        tripId: newTrip.id,
        userId: user?.sub, // Use the same ID format as in createdBy
        role: TripRole.PARTICIPANT // Set role as PARTICIPANT
      };
      
      try {
        const memberResult = await client.graphql({
          query: createTripMember,
          variables: { input: tripMemberInput },
          authMode: "userPool"
        });
        
       
        const tripData: any = await client.graphql({
          query: getTrip, 
          variables: { id: newTrip.id }, 
          authMode: "userPool"
        });
        

      } catch (memberError) {
        console.error("Error creating TripMember:", memberError);
        // Continue execution even if member creation fails
      }
    
      navigate(`/trips/${newTrip.id}`);
    } catch (error) {
      console.error('Error creating trip:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value
    });
  };

  return (
    <View padding="2rem" backgroundColor="#121212" color="white">
      {/* Back Button */}
      <Button
        onClick={() => navigate('/trips')}
        variation="link"
        marginBottom="1.5rem"
        color="#AAAAAA"
        className="flex items-center hover:text-white"
      >
        <ChevronLeft size={20} className="mr-1" />
        BACK TO ALBUMS
      </Button>
      
      {/* Header */}
      <Flex direction="column" alignItems="center" marginBottom="2.5rem">
        <Text fontSize="0.9rem" color="#777777" letterSpacing="0.15em" textTransform="uppercase" marginBottom="0.5rem">New Production</Text>
        <Heading 
          level={1} 
          color="white" 
          textTransform="uppercase" 
          letterSpacing="0.1em" 
          fontSize="2.5rem" 
          fontWeight="bold"
          textAlign="center"
        >
          CREATE NEW ALBUM
        </Heading>
        <Divider 
          marginTop="1.5rem" 
          width="6rem"
          style={{ backgroundColor: '#404040', height: '2px' }} 
        />
      </Flex>
      
      {/* Main Form */}
      <form onSubmit={handleSubmit}>
        <View 
          backgroundColor="#1A1A1A" 
          padding="2rem" 
          borderRadius="4px" 
          border="1px solid #303030"
          maxWidth="800px"
          marginLeft="auto"
          marginRight="auto"
          boxShadow="0 4px 15px rgba(0, 0, 0, 0.3)"
        >
          <Flex direction="column" gap="1.5rem">
            {/* Trip Title Input */}
            <View>
              <Flex alignItems="center" marginBottom="0.5rem">
                <Film size={16} className="text-blue-400 mr-2" />
                <Text 
                  as="label" 
                  htmlFor="tripName" 
                  fontSize="0.8rem" 
                  fontWeight="bold" 
                  color="#BBBBBB" 
                  letterSpacing="0.1em" 
                  textTransform="uppercase"
                >
                  Album Title
                </Text>
              </Flex>
              <TextField
                id="tripName"
                name="tripName"
                label="Album Name"
                placeholder="Enter a memorable title for your album"
                value={formState.tripName}
                onChange={handleChange}
                isRequired
                hasError={!!errors.tripName}
                errorMessage={errors.tripName}
                backgroundColor="#202020"
                color="white"
                borderColor="#404040"
                fontSize="1.1rem"
                padding="1rem"
                width="100%"
              />
              {errors.tripName && (
                <Text color="#FF4444" fontSize="0.8rem" marginTop="0.5rem">
                  {errors.tripName}
                </Text>
              )}
            </View>
            
            {/* Description Input */}
            <View>
              <Flex alignItems="center" marginBottom="0.5rem">
                <AlignLeft size={16} className="text-blue-400 mr-2" />
                <Text 
                  as="label" 
                  htmlFor="description" 
                  fontSize="0.8rem" 
                  fontWeight="bold" 
                  color="#BBBBBB" 
                  letterSpacing="0.1em" 
                  textTransform="uppercase"
                >
                  Synopsis
                </Text>
              </Flex>
              <TextAreaField
                id="description"
                name="description"
                label="Description"
                placeholder="Describe your Album - what made it special and what you want to remember"
                value={formState.description}
                onChange={handleChange}
                rows={4}
                backgroundColor="#202020"
                color="white"
                borderColor="#404040"
                fontSize="0.95rem"
                padding="1rem"
                width="100%"
              />
            </View>
            
            <Divider 
              marginTop="1rem" 
              marginBottom="1rem" 
              style={{ backgroundColor: '#303030' }} 
            />
            
            {/* Action Buttons */}
            <Flex direction="row" justifyContent="center" gap="1.5rem" marginTop="1rem">
              <Button
                type="button"
                onClick={() => navigate('/trips')}
                className="px-6 py-2 rounded border border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white uppercase tracking-wide text-sm transition-colors"
              >
                Cancel
              </Button>
              
              <Button
                type="submit"
                isLoading={loading}
                loadingText="Creating..."
                className="px-8 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 uppercase tracking-wide text-sm font-medium transition-colors"
              >
                {loading ? "Creating..." : "Create Album"}
              </Button>
            </Flex>
          </Flex>
        </View>
      </form>
      
      {/* Cinematic Footer */}
      <Flex direction="column" alignItems="center" marginTop="4rem" opacity="0.6">
        <Text fontSize="0.7rem" color="#777777" letterSpacing="0.1em" textTransform="uppercase">
          BANJARA PRODUCTIONS
        </Text>
        <Text fontSize="0.7rem" color="#777777" marginTop="0.25rem">
          SCENE {Math.floor(Math.random() * 100)} • TAKE {Math.floor(Math.random() * 10)}
        </Text>
      </Flex>
    </View>
  );
};

export default CreateTripPage;